import dayjs from 'dayjs';

import type { GarmentMovementDayDetail } from '@/api/reports';
import { EmptyState } from '@/components/PageHeading';
import { hueClasses } from '@/components/hues';
import { useEscapeKey } from '@/components/shortcuts';
import type { GarmentMovementCounts } from '@core/api/reports';
import {
  dayFiguresOf,
  dayMovedSentence,
  dayPapers,
  sideCounts,
  type MovedPaper,
  type MoveSide,
} from '@core/reports/garmentMovement';

/** Each card's label in its queue's hue — the cleaner cyan, the tailor purple, new stock orange. */
const labelInk: Record<MovedPaper['kind'], string> = {
  HandedOver: 'text-default',
  TakenBack: 'text-default',
  ToCleaner: hueClasses.cyan.ink,
  FromCleaner: hueClasses.cyan.ink,
  ToTailor: hueClasses.purple.ink,
  FromTailor: hueClasses.purple.ink,
  BookedIn: hueClasses.orange.ink,
};

const sides: Record<MoveSide, { title: string; empty: string; icon: string; tile: string }> = {
  out: {
    title: 'Left the shop',
    empty: 'Nothing left the shop.',
    icon: 'icon-arrow-up-right',
    tile: `${hueClasses.secondary.wash} ${hueClasses.secondary.ink}`,
  },
  in: {
    title: 'Came into the shop',
    empty: 'Nothing came in.',
    icon: 'icon-arrow-down-left',
    tile: `${hueClasses.primary.wash} ${hueClasses.primary.ink}`,
  },
};

/**
 * A day of the register opened — direction D of canvas "Daily garment in/out" (owner, 2026-10-29):
 * the band says how many garments left the shop and how many came in, with the counter, cleaner,
 * tailor and new stock as figures; under it the two sides, each paper that went with the garments
 * once, naming whom, with the garments beneath. Figures are the register row's (the server's);
 * the cards are the core's `dayPapers` over the day's read.
 *
 * <p>
 * Shaped like the booking screens' `Dialog` — the overlay tokens, the shortcut layer, Esc closes —
 * and mounted where the page content is, which keeps it above the sidebar.
 * </p>
 */
export function DayMovesDialog({
  day,
  counts,
  branchName,
  detail,
  loading,
  onClose,
  onOpenBooking,
}: {
  day: string;
  counts: GarmentMovementCounts;
  branchName: string;
  detail: GarmentMovementDayDetail | undefined;
  loading: boolean;
  onClose: () => void;
  onOpenBooking: (rentalOrderId: number) => void;
}) {
  useEscapeKey(onClose);

  const title = dayMovedSentence(counts);
  const totals = sideCounts(counts);
  const papers = detail ? dayPapers(detail) : null;

  return (
    <div
      data-shortcut-layer
      className="fixed inset-0 z-9999 flex items-start justify-center p-4 sm:pt-10 print:hidden"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-[var(--overlay-scrim)]"
      />

      <section
        role="dialog"
        aria-label={`${dayjs(day).format('dddd D MMMM')} — ${title}`}
        className="relative flex max-h-[calc(100vh-2rem)] w-full max-w-[960px] flex-col overflow-hidden rounded-2xl border border-[var(--overlay-border)] bg-[var(--overlay-surface)] shadow-lg sm:max-h-[calc(100vh-5rem)]"
      >
        <header className="flex flex-col gap-4 bg-[var(--color-hero)] px-5 pt-5 pb-5 text-[#ffffff] sm:px-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="page-eyebrow mb-1">
                {dayjs(day).format('dddd D MMMM')}
                {branchName ? ` · ${branchName}` : ''}
              </p>
              <h2 className="mb-0 font-serif text-[24px] leading-tight font-semibold text-[#ffffff] sm:text-[26px]">
                {title}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-[#ffffff]/40 text-[#ffffff] hover:bg-[#ffffff]/15"
            >
              <i className="icon icon-x text-[18px]" aria-hidden="true" />
            </button>
          </div>

          <ul className="m-0 grid grid-cols-2 gap-2.5 ps-0 md:grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
            {dayFiguresOf(counts).map((figure) => (
              <li
                key={figure.label}
                className="mb-0 list-none rounded-xl bg-[#ffffff]/10 px-3.5 py-2.5"
              >
                <span className="block text-[12px] text-(--color-hero-muted)">{figure.label}</span>
                <span className="font-serif text-2xl font-semibold lining-nums tabular-nums">
                  {figure.value}
                </span>
              </li>
            ))}
          </ul>
        </header>

        <div className="overflow-y-auto px-5 pt-5 pb-6 sm:px-6">
          {loading ? (
            <p className="text-default mb-0 text-xs">Reading the day…</p>
          ) : !papers ? (
            <EmptyState icon="icon icon-shirt" title="The day could not be read" />
          ) : (
            <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2">
              {(['out', 'in'] as const).map((side) => (
                <section key={side} className="flex flex-col gap-3">
                  <h3 className="mb-0 flex items-center gap-2 text-[15px] font-bold text-gray-900">
                    <span
                      className={`flex size-7 items-center justify-center rounded-lg ${sides[side].tile}`}
                    >
                      <i className={`icon ${sides[side].icon} text-[16px]`} aria-hidden="true" />
                    </span>
                    {sides[side].title} · {totals[side]}
                  </h3>

                  {papers[side].length === 0 ? (
                    <p className="text-default mb-0 text-xs">{sides[side].empty}</p>
                  ) : (
                    papers[side].map((paper) => (
                      <PaperCard key={paper.key} paper={paper} onOpenBooking={onOpenBooking} />
                    ))
                  )}
                </section>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

/** One paper: what it was and when, whom it names and its number, then its garments. */
function PaperCard({
  paper,
  onOpenBooking,
}: {
  paper: MovedPaper;
  onOpenBooking: (rentalOrderId: number) => void;
}) {
  const rentalOrderId = paper.rentalOrderId;

  return (
    <article className="border-border-color flex flex-col gap-2 rounded-xl border px-3.5 py-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className={`text-[12px] font-semibold ${labelInk[paper.kind]}`}>{paper.label}</span>
        {paper.time && <span className="text-default text-[12px]">{paper.time}</span>}
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 text-[15px] font-bold text-gray-900">{paper.who}</span>
        {rentalOrderId !== null ? (
          <button
            type="button"
            onClick={() => onOpenBooking(rentalOrderId)}
            className="text-primary shrink-0 cursor-pointer text-[13px] font-semibold hover:underline"
          >
            {paper.documentNumber ?? 'Booking'} ›
          </button>
        ) : (
          paper.documentNumber && (
            <span className="text-default shrink-0 text-[13px] font-semibold">
              {paper.documentNumber}
            </span>
          )
        )}
      </div>

      {paper.runTogether ? (
        <p className="text-default mb-0 text-[13px]">
          {/* A line breaks between codes, never inside one. */}
          {paper.garments.map((garment, index) => (
            <span key={garment.key}>
              <span className="font-mono-nums font-semibold whitespace-nowrap text-gray-900">
                {garment.code}
              </span>
              {index < paper.garments.length - 1 ? ', ' : ' '}
            </span>
          ))}
          <span className="whitespace-nowrap">— {paper.garments.length} garments</span>
        </p>
      ) : (
        <ul className="m-0 flex flex-col gap-1 ps-0">
          {paper.garments.map((garment) => (
            <li key={garment.key} className="mb-0 list-none text-[13px]">
              <span className="font-mono-nums font-semibold text-gray-900">{garment.code}</span>
              <span className="text-default"> · {garment.design}</span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
